import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { IoMdClose } from "react-icons/io";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { Descriptions } from "../../../../types/budget/service-item";
import { useEffect, useRef, useState } from "react";
import { LabeledInputFull } from "../../../UI/LabeledInputFull";
import { MdExpandLess, MdExpandMore } from "react-icons/md";
import { useTextareaOverflow } from "../../../novos/Budget/ServiceItem";

type ExpandedField = "composition" | "cancelPolicy" | "paymentMethod" | null;

interface DescriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  descriptions: Descriptions;
  onChange(descriptions: Descriptions): void;
  onConfirm(): void;
  hideComposition?: boolean;
}

const DescriptionModal = ({
  onClose,
  isOpen,
  descriptions,
  onChange,
  onConfirm,
  hideComposition = false,
}: DescriptionModalProps) => {
  const [descriptionState, setDescriptionState] = useState<null | Descriptions>(
    null
  );
  const [expandedField, setExpandedField] = useState<ExpandedField>(null);

  const commercialConditionsEnabled =
    descriptions.commercialConditions ?? false;

  const compositionRef = useRef<HTMLTextAreaElement>(null);
  const cancelPolicyRef = useRef<HTMLTextAreaElement>(null);
  const paymentMethodRef = useRef<HTMLTextAreaElement>(null);
  const prevIsOpenRef = useRef(false);

  // Sincroniza descriptionState ao abrir o modal (cada service item tem seus próprios dados)
  useEffect(() => {
    if (isOpen) {
      if (!prevIsOpenRef.current) {
        setDescriptionState(descriptions);
      }
      prevIsOpenRef.current = true;
    } else {
      setDescriptionState(null);
      setExpandedField(null);
      prevIsOpenRef.current = false;
    }
  }, [isOpen, descriptions]);

  const hasOverflowComposition = useTextareaOverflow(
    compositionRef,
    descriptions.composition
  );
  const hasOverflowCancelPolicy = useTextareaOverflow(
    cancelPolicyRef,
    descriptions.cancelPolicy
  );
  const hasOverflowPaymentMethod = useTextareaOverflow(
    paymentMethodRef,
    descriptions.paymentMethod
  );

  useEffect(() => {
    if (!descriptionState) {
      setDescriptionState(descriptions);
    }
  }, [descriptions, descriptionState]);

  const handleClose = () => {
    onChange(descriptionState!);
    setDescriptionState(null);
    setExpandedField(null);
    onClose();
  };

  const handleConfirm = () => {
    onConfirm();
    setDescriptionState(null);
    setExpandedField(null);
    onClose();
  };

  return (
    <Dialog onClose={handleClose} open={isOpen} fullWidth maxWidth={"sm"}>
      <DialogTitle sx={{ m: 0, px: 4, color: "#4B465C" }}>
        Descritivos
      </DialogTitle>

      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 16,
          top: 16,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <IoMdClose />
      </IconButton>
      <DialogContent sx={{ px: 4 }}>
        <Box display={"flex"} flexDirection={"column"} gap={2}>
          {!hideComposition && (
            <LabeledInputFull
              label="Composição"
              value={descriptions.composition}
              placeholder="Especifique"
              multiline
              maxRows={expandedField === "composition" ? 25 : 1}
              inputRef={compositionRef}
              onChange={(e) =>
                onChange({
                  ...descriptions,
                  composition: e.target.value,
                })
              }
              onFocus={() => setExpandedField("composition")}
              onBlur={() => setExpandedField(null)}
              endAdornment={
                hasOverflowComposition && descriptions.composition?.trim() ? (
                  expandedField === "composition" ? (
                    <MdExpandLess
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        compositionRef.current?.blur();
                        setExpandedField(null);
                      }}
                    />
                  ) : (
                    <MdExpandMore
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setExpandedField("composition")}
                    />
                  )
                ) : null
              }
            />
          )}
          {!hideComposition && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={commercialConditionsEnabled}
                  onChange={(e) =>
                    onChange({
                      ...descriptions,
                      commercialConditions: e.target.checked,
                    })
                  }
                />
              }
              label="Habilitar Condições Comerciais"
              sx={{ "& .MuiFormControlLabel-label": { fontSize: 13 } }}
            />
          )}
          <LabeledInputFull
            label="Política de cancelamento"
            disabled={!hideComposition && !commercialConditionsEnabled}
            value={descriptions.cancelPolicy}
            placeholder="Especifique"
            multiline
            maxRows={expandedField === "cancelPolicy" ? 25 : 1}
            inputRef={cancelPolicyRef}
            onChange={(e) =>
              onChange({
                ...descriptions,
                cancelPolicy: e.target.value,
              })
            }
            onFocus={() => setExpandedField("cancelPolicy")}
            onBlur={() => setExpandedField(null)}
            endAdornment={
              hasOverflowCancelPolicy &&
              descriptions.cancelPolicy?.trim() &&
              (hideComposition || commercialConditionsEnabled) ? (
                expandedField === "cancelPolicy" ? (
                  <MdExpandLess
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      cancelPolicyRef.current?.blur();
                      setExpandedField(null);
                    }}
                  />
                ) : (
                  <MdExpandMore
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setExpandedField("cancelPolicy")}
                  />
                )
              ) : null
            }
          />
          <LabeledInputFull
            label="Forma de pagamento"
            disabled={!hideComposition && !commercialConditionsEnabled}
            value={descriptions.paymentMethod}
            placeholder="Especifique"
            multiline
            maxRows={expandedField === "paymentMethod" ? 25 : 1}
            inputRef={paymentMethodRef}
            onChange={(e) =>
              onChange({
                ...descriptions,
                paymentMethod: e.target.value,
              })
            }
            onFocus={() => setExpandedField("paymentMethod")}
            onBlur={() => setExpandedField(null)}
            endAdornment={
              hasOverflowPaymentMethod &&
              descriptions.paymentMethod?.trim() &&
              (hideComposition || commercialConditionsEnabled) ? (
                expandedField === "paymentMethod" ? (
                  <MdExpandLess
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      paymentMethodRef.current?.blur();
                      setExpandedField(null);
                    }}
                  />
                ) : (
                  <MdExpandMore
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setExpandedField("paymentMethod")}
                  />
                )
              ) : null
            }
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 4, py: 3 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancelar
        </Button>
        <Button onClick={handleConfirm}>Salvar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DescriptionModal;
